import { useCallback, useMemo, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { Save } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import {
  Activity_Types_Enum_Enum,
  Leads_Status_Enum,
} from '@graphql/__generated__/graphql';
import { CreateListingModal } from '@modules/_shared/components/create-listing/CreateListingModal';
import IndicatorLeadStage from '@modules/_shared/components/IndicatorLeadStage';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import RASnackbar from '@modules/_shared/components/RASnackbar';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { GET_LEAD } from '@modules/leads/leadsQueries';
import { useLocale } from '@shared/hooks/locale';

const GET_PIPELINES = gql(/* GraphQL */ `
  query GetPipelines($leadType: lead_type!) {
    pipelines(where: { lead_type: { _eq: $leadType } }) {
      id
      label
      stages(order_by: { order_nr: asc }, where: { name: { _neq: "new" } }) {
        id
        label
        status
      }
    }
  }
`);

const UPDATE_MULTIPLE_LEADS_STAGE = gql(/* GraphQL */ `
  mutation UpdateMultipleLeadsStage(
    $updates: [lead_agents_updates!]!
    $activities: [activities_insert_input!]!
  ) {
    update_lead_agents_many(updates: $updates) {
      returning {
        id
        lead_id
        user_id
        stage_id
        lead_stage {
          id
          label
        }
      }
    }
    insert_activities(objects: $activities) {
      returning {
        id
      }
    }
  }
`);

type DialogUpdatePipelineContentProps = Omit<DialogUpdatePipelineProps, 'open'>;

const DialogUpdatePipelineContent = ({
  leadsIds,
  pipeline,
  initialPipelineId,
  initialStageId,
  onCancel,
  updateCacheFn,
}: DialogUpdatePipelineContentProps) => {
  const { t } = useLocale();
  const { me } = useAppData();
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);

  const [form, setForm] = useState({
    pipelineId: initialPipelineId ?? '',
    stageId: initialStageId ?? '',
  });

  const [targetStage, setTargetStage] = useState<{
    status: Leads_Status_Enum;
    stageId: string;
    pipelineId: string;
  } | null>(null);
  const [multiWonWarning, setMultiWonWarning] = useState(false);

  const { data } = useQuery(GET_PIPELINES, {
    variables: { leadType: pipeline },
  });

  const [updateMultipleLeadsStage, { loading: updateLoading }] = useMutation(
    UPDATE_MULTIPLE_LEADS_STAGE,
    { onError: setErrorLike },
  );
  const [getLead] = useLazyQuery(GET_LEAD);

  const pipelines = useMemo(() => data?.pipelines ?? [], [data]);

  const stages = useMemo(
    () =>
      (pipelines.length === 1
        ? pipelines[0]
        : pipelines.find(p => p.id === form.pipelineId)
      )?.stages ?? [],
    [pipelines, form.pipelineId],
  );

  const updateLeadsStage = useCallback(
    async (stageId: string, note?: string) =>
      await updateMultipleLeadsStage({
        variables: {
          updates: leadsIds.map(leadId => ({
            where:
              typeof leadId === 'string'
                ? {
                    lead_id: { _eq: leadId },
                  }
                : {
                    lead_id: { _eq: leadId.leadId },
                    ...(me?.is_admin
                      ? {
                          user: {
                            teams_users: {
                              team: {
                                teams_users: {
                                  user_id: { _eq: leadId.agentId },
                                },
                              },
                            },
                          },
                        }
                      : {}),
                  },
            _set: { stage_id: stageId },
          })),
          activities: note
            ? leadsIds.map(leadId => ({
                lead_id: typeof leadId === 'string' ? leadId : leadId.leadId,
                activity_type: Activity_Types_Enum_Enum.Note,
                note,
                success: false,
                done: true,
                done_at: new Date().toISOString(),
                ...(me?.is_admin ? { created_by: me?.id } : {}),
              }))
            : [],
        },
      }),
    [updateMultipleLeadsStage, leadsIds, me?.is_admin, me?.id],
  );

  const handleClose = useCallback(() => {
    setTargetStage(null);
    setMultiWonWarning(false);
    onCancel();
  }, [onCancel]);

  const onSubmit = useCallback(async () => {
    const selectedStage = stages.find(s => s.id === form.stageId);
    if (!selectedStage) {
      return;
    }

    if (selectedStage.status === Leads_Status_Enum.Won && leadsIds.length > 1) {
      setMultiWonWarning(true);
      return;
    }

    updateCacheFn({
      stageId: selectedStage.id,
      leadIds: leadsIds,
    });

    if (selectedStage.status === Leads_Status_Enum.Won) {
      // We have only one lead here, so we can use the lead_id directly.
      const lead = await getLead({
        variables: {
          lead_id:
            typeof leadsIds[0] === 'string' ? leadsIds[0] : leadsIds[0].leadId,
        },
      });

      // We have a listing, so we don't need to create a listing
      // just update the stage and close the dialog
      if (lead.data?.leads_by_pk?.lots.length !== 0) {
        await updateLeadsStage(selectedStage.id);
        handleClose();
        return;
      }

      setTargetStage({
        status: Leads_Status_Enum.Won,
        stageId: selectedStage.id,
        pipelineId: form.pipelineId,
      });
    } else {
      let note;
      if (selectedStage.status === Leads_Status_Enum.Lost) {
        note = selectedStage.label ?? '';
      }

      await updateLeadsStage(selectedStage.id, note);
      handleClose();
    }
  }, [
    stages,
    leadsIds,
    updateCacheFn,
    form.stageId,
    form.pipelineId,
    getLead,
    updateLeadsStage,
    handleClose,
  ]);

  const isLoading = updateLoading;

  return (
    <>
      <DialogTitle>
        {pipelines.length > 1
          ? t('Change pipeline and stage')
          : t('Change stage')}
      </DialogTitle>
      <DialogContent>
        {pipelines.length > 1 && (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <FormLabel style={{ marginBottom: '0.5rem', fontWeight: 500 }}>
              {t('Pipeline')}
            </FormLabel>
            <Select
              fullWidth
              variant="outlined"
              size="small"
              sx={{ background: 'var(--mui-palette-background-paper)' }}
              value={form.pipelineId}
              onChange={event => {
                setForm({
                  pipelineId: event.target.value as string,
                  stageId: '',
                });
              }}
            >
              {pipelines.map(pipeline => (
                <MenuItem key={pipeline.id} value={pipeline.id}>
                  {pipeline.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <FormControl fullWidth>
          <FormLabel style={{ marginBottom: '0.5rem', fontWeight: 500 }}>
            {t('Stage')}
          </FormLabel>
          <Select
            fullWidth
            variant="outlined"
            size="small"
            sx={{ background: 'var(--mui-palette-background-paper)' }}
            value={form.stageId}
            onChange={event => {
              setForm(prev => ({
                ...prev,
                stageId: event.target.value as string,
              }));
            }}
          >
            {stages.map(stage => (
              <MenuItem key={stage.id} value={stage.id}>
                <IndicatorLeadStage status={stage.status}>
                  {stage.label}
                </IndicatorLeadStage>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ pb: 2, px: 3 }}>
        <Button onClick={handleClose}>{t('Cancel')}</Button>
        <LoadingButton
          onClick={onSubmit}
          loadingPosition="start"
          startIcon={<Save />}
          loading={isLoading}
          color="primary"
          variant="contained"
          disabled={!form.stageId}
        >
          {t('Save')}
        </LoadingButton>
      </DialogActions>

      {targetStage?.status === 'won' && (
        <CreateListingModal
          fromLeadId={
            typeof leadsIds[0] === 'string' ? leadsIds[0] : leadsIds[0].leadId
          }
          brokerId={
            typeof leadsIds[0] === 'string' ? undefined : leadsIds[0].agentId
          }
          open
          onClose={handleClose}
        />
      )}

      {multiWonWarning && (
        <RASnackbar
          message={t(
            'Converting multiple leads to listings must be done individually',
          )}
          onClose={() => setMultiWonWarning(false)}
        />
      )}

      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setErrorLike(null)}
      />
    </>
  );
};

type DialogUpdatePipelineProps = {
  leadsIds: string[] | { leadId: string; agentId: string }[];
  pipeline: string;
  initialPipelineId?: string | null;
  initialStageId?: string | null;
  open: boolean;
  onCancel: () => void;
  updateCacheFn: (args: {
    stageId: string;
    leadIds: string[] | { leadId: string; agentId: string }[];
  }) => void;
};

export const DialogUpdatePipeline = ({
  open,
  onCancel,
  ...props
}: DialogUpdatePipelineProps) => (
  <Dialog
    open={open}
    onClose={onCancel}
    slotProps={{
      paper: {
        sx: {
          width: '100%',
          maxWidth: 400,
          margin: '0 auto',
          zIndex: 15000,
        },
      },
    }}
  >
    <DialogUpdatePipelineContent {...props} onCancel={onCancel} />
  </Dialog>
);

export default DialogUpdatePipeline;
